/**
 * What a host's per-claim dinner edit answered, turned into something they can
 * act on while standing in a restaurant.
 *
 * Sibling of `dinnerClaim.ts`, deliberately not merged with it. That file words
 * the ADD path; this one words the EDIT path — release, rename, resize, move —
 * and the same wire status needs a different sentence on each. `full` on add
 * says "Nobody was added"; on a resize it says "stays at 3"; on a move it says
 * "nothing moved — they are still at Belcanto". One table cannot say all three,
 * and a table that tries ends up saying the vaguest of them.
 *
 * What both files share is the rule about rules: the server owns capacity, the
 * party cap and one-table-per-guest-per-evening, and nothing here recomputes
 * any of them. This module reads the server's answer out loud. The one number
 * it is handed rather than derives is `hold.maxPartySize`, which is relative to
 * the table and therefore not computable here at all.
 *
 * Two things about the copy are load-bearing rather than stylistic:
 *
 *  - EVERY move refusal says "nothing moved" and names the table the guest is
 *    still sitting at. A red alert after a move otherwise reads as "the seat is
 *    gone from both tables", and the host's next move is to re-seat a guest who
 *    never got up.
 *  - The MOVE transport failure says something different from every other
 *    transport failure, because the server may have committed before the socket
 *    died. "Check your signal and try again" is safe advice for a release and
 *    dangerous advice for a move.
 */
import { mutationErrorMessage } from './mutation-error';
import { isoToWallClock } from './tripTime';
import type { DinnerNoteSeverity } from './dinnerClaim';
import type { GroupDinnerClaim, GroupDinnerHold, GroupInvitee } from '../types/groups';

export type { DinnerNoteSeverity };

/** Something the host can do about the note, rendered as a button on the alert. */
export interface ClaimEditAction {
  label: string;
  /**
   * `open-conflict` hops to the guest's other seat, which is the entire payoff
   * of host release existing: the one-table-per-evening refusal stops being a
   * dead end and becomes a two-tap fix. `open-guests` sends a blocked rename to
   * the tab that owns the name. `reload` re-reads the tables after a move whose
   * outcome nobody knows.
   */
  kind: 'open-conflict' | 'open-guests' | 'reload';
  /** `open-conflict` only: the table the guest's other seat is on. */
  holdId?: string | null;
}

export interface ClaimEditNote {
  severity: DinnerNoteSeverity;
  message: string;
  /** The wire status behind the note, '' when the failure carried none. */
  status: string;
  action?: ClaimEditAction;
}

/**
 * Everything the copy needs that is not on the wire.
 *
 * `name` is the name as it stood BEFORE the write, which is how a rename is
 * detected without the client having to remember what it asked for.
 * `destinationVenue` is what makes an outcome a MOVE — it is the only field set
 * on exactly the move path, so it doubles as the discriminator and there is no
 * second verb flag that can disagree with it.
 */
export interface ClaimEditContext {
  name: string;
  firstName: string;
  /** The table the claim was on when the host acted. */
  sourceVenue: string;
  /** Set on a move and only on a move. */
  destinationVenue?: string | null;
  previousPartySize: number;
  hasSeatingChart: boolean;
}

/**
 * Statuses that leave the hold list on screen behind the database.
 *
 * `full` belongs here more than any other. It is the server saying the number
 * on screen is too high — so refusing without re-reading leaves the stepper
 * still offering the size that was just refused, and the move picker still
 * showing the seat that was just taken. The host taps, reads a refusal, and
 * taps the identical control again. Anything that changed a table, plus
 * anything that proves the list is behind, and `full` is the second kind.
 */
const NEEDS_REFETCH = new Set([
  'released',
  'moved',
  'updated',
  'not_found',
  'already_released',
  'already_claimed',
  'full',
]);

/**
 * Statuses after which the sheet is about a guest who is not on this table any
 * more. Leaving it open would leave a ghost: controls that act on a row that
 * moved, or that is gone.
 */
const CLOSES_SHEET = new Set(['released', 'moved', 'not_found', 'already_released']);

export function editNoteRequiresRefetch(note: ClaimEditNote): boolean {
  return NEEDS_REFETCH.has(note.status);
}

export function editNoteClosesSheet(note: ClaimEditNote): boolean {
  return CLOSES_SHEET.has(note.status);
}

function spots(n: number): string {
  return `${n} spot${n === 1 ? '' : 's'}`;
}

function people(n: number): string {
  return n === 1 ? 'person' : 'people';
}

function text(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function asCount(value: unknown): number | null {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** "Take Ana off" reads better on a button than "Take Ana Silva Ferreira off". */
export function firstNameOf(name: string): string {
  const trimmed = (name || '').trim();
  if (!trimmed) return 'them';
  return trimmed.split(/\s+/)[0];
}

/**
 * The name to show for a seat.
 *
 * The roster row wins whenever the claim is linked to one, because that is what
 * the seating chart already renders (`COALESCE(NULLIF(invitee.full_name,''),
 * claim.guest_name)` on the server) and because it is the name a rename would
 * have to change. A claim row carrying a stale copy of somebody's name must
 * never put two names on one person across two screens.
 */
export function claimDisplayName(
  claim: Pick<GroupDinnerClaim, 'inviteeId' | 'guestName'>,
  roster: readonly GroupInvitee[],
): string {
  if (claim.inviteeId) {
    const match = roster.find((invitee) => invitee.id === claim.inviteeId);
    const rosterName = text(match?.fullName);
    if (rosterName) return rosterName;
  }
  return text(claim.guestName) || 'Guest';
}

export interface ClaimEditCapabilities {
  canRename: boolean;
  renameBlockedReason: string | null;
}

/**
 * Whether this seat's name can be edited here, and what to say when it cannot.
 *
 * Only an unlinked claim — a `+1` the roster never matched — is renameable. For
 * a linked claim the seating chart already shows the ROSTER name, so a rename
 * here would change the dinner list and silently not change the seating chart:
 * one person under two names on two screens on the night. The server refuses it
 * with 409 `linked_to_invitee`; this is the same refusal said before the tap.
 *
 * The reason is returned rather than the control being hidden. A missing button
 * reads as a bug, and a host hunting for it taps the controls either side of
 * where it should have been — one of which takes a seat away.
 */
export function claimEditCapabilities(
  claim: Pick<GroupDinnerClaim, 'inviteeId'>,
  rosterName?: string,
): ClaimEditCapabilities {
  if (!claim.inviteeId) return { canRename: true, renameBlockedReason: null };
  const named = text(rosterName);
  return {
    canRename: false,
    renameBlockedReason:
      (named ? `On the guest list as ${named}.` : 'On the guest list, so the name comes from there.') +
      ' Change it on the Guests tab and it changes everywhere.',
  };
}

export interface PartySizeOption {
  size: number;
  disabled: boolean;
  /** Why this size is not offered, shown inline. Null when it is offered. */
  reason: string | null;
}

/**
 * The party sizes this seat can be set to, and which of them the table cannot
 * currently absorb.
 *
 * The ceiling is the server's `maxPartySize` for THIS table, rendered and never
 * derived. The disabling is arithmetic on two numbers the server sent — how
 * much bigger the party would get, against how much room is left — not a second
 * copy of the capacity rule; the server still answers `full`, and this only
 * keeps the host from tapping a size it will refuse.
 *
 * A hold from an API that predates `maxPartySize` falls back to the size the
 * party already is, so the stepper shows the truth and offers no growth rather
 * than inventing a cap. Studio ships to staging before the API tag lands, and
 * an undefined ceiling would otherwise render an empty control.
 */
export function partySizeOptions(
  hold: Pick<GroupDinnerHold, 'maxPartySize' | 'availableSpots'>,
  claim: Pick<GroupDinnerClaim, 'partySize'>,
): PartySizeOption[] {
  const current = Math.max(1, Math.floor(Number(claim.partySize) || 1));
  const sent = Number(hold.maxPartySize);
  const ceiling = Number.isFinite(sent) && sent >= 1 ? Math.floor(sent) : current;
  const available = Math.max(0, Number(hold.availableSpots) || 0);

  const options: PartySizeOption[] = [];
  for (let size = 1; size <= Math.max(ceiling, current); size += 1) {
    const growth = size - current;
    const disabled = growth > available;
    options.push({
      size,
      disabled,
      reason: disabled ? `Only ${spots(available)} left at this table.` : null,
    });
  }
  return options;
}

export interface RemoveConfirmCopy {
  title: string;
  body: string;
  cancelLabel: string;
  confirmLabel: string;
}

/**
 * The per-claim destructive confirm.
 *
 * It has to be impossible to mistake for the hold delete, and wording alone
 * cannot carry that: on iOS a `window.confirm` renders with generic OK/Cancel
 * buttons, so two identical grey boxes differing by one sentence are the same
 * box. This one is a step inside the sheet with its own buttons, and it differs
 * from the hold delete by widget, by verb ("take off" vs "delete"), by noun (a
 * person vs the table) and by button label. `holdDeleteConfirmCopy` is the
 * other half of that pair — read them together.
 */
export function removeConfirmCopy(
  claim: Pick<GroupDinnerClaim, 'guestName'>,
  hold: Pick<GroupDinnerHold, 'venueName'>,
  otherCount: number,
  displayName?: string,
): RemoveConfirmCopy {
  const name = text(displayName) || text(claim.guestName) || 'this guest';
  const venue = text(hold.venueName) || 'this';
  const others = Math.max(0, Math.floor(Number(otherCount) || 0));

  return {
    title: `Take ${name} off this table?`,
    body:
      others > 0
        ? `Only ${name}'s seat. The ${venue} table and the other ${others} ${people(others)} on it` +
          ' stay exactly as they are.'
        : `Only ${name}'s seat. The ${venue} table itself stays — nobody else is on it.`,
    cancelLabel: 'Keep their seat',
    confirmLabel: `Take ${firstNameOf(name)} off`,
  };
}

/**
 * The hold delete's confirm, rewritten because the old one became actively
 * dangerous the day per-claim removal shipped.
 *
 * It used to read "Any claims are removed too." Once there is a way to take ONE
 * guest off a table, an abstract "any claims" reads like the way to do it — and
 * a host who is looking for that button and finds this one loses the whole
 * guest list on the night. Naming the count ("all 6 people") is what stops the
 * misfire; "any claims" never did.
 *
 * It also has to keep saying that deleting is not a route to a bigger table.
 * The hold API is still POST and DELETE only: there is no way to enlarge a
 * table that exists, and DELETE cancels every claim on it on the way out.
 */
export function holdDeleteConfirmCopy(
  hold: Pick<GroupDinnerHold, 'venueName' | 'claims' | 'claimedSpots'>,
): string {
  const venue = text(hold.venueName) || 'this';
  // People, not rows. A claim carries a party size, so three rows of two is six
  // seats — and counting rows understates the damage by exactly the factor that
  // matters on a night with parties, in the direction that makes the delete look
  // cheaper than it is. `claimedSpots` is the server's own SUM(party_size);
  // summing the rows is the fallback for a payload that predates it.
  const count = peopleOnHold(hold);
  return count > 0
    ? `Delete the ${venue} table? All ${count} ${people(count)} on it lose their seats —` +
        ' this is not how you take one guest off.'
    : `Delete the ${venue} table? Nobody has claimed it.`;
}

/**
 * How many people are sitting at a table.
 *
 * Not `claims.length`. A claim is one booking with a party size, so three
 * claims of two is six chairs — the type says as much: "the party sizes sum to
 * claimedSpots". Every sentence that states a blast radius has to count this
 * way, because a host reading "3 people" and stranding 6 is the misfire those
 * sentences exist to prevent.
 */
export function peopleOnHold(hold: Pick<GroupDinnerHold, 'claims' | 'claimedSpots'>): number {
  const reported = Math.floor(Number(hold.claimedSpots) || 0);
  if (reported > 0) return reported;
  return sumParties(hold.claims);
}

/** The same count, minus one claim: who stays when that guest comes off. */
export function peopleBesides(
  hold: Pick<GroupDinnerHold, 'claims' | 'claimedSpots'>,
  claimId: string,
): number {
  const others = (hold.claims || []).filter((row) => row.id !== claimId);
  // Prefer the server's total minus this claim, so the two numbers in the sheet
  // ("N others stay", "M on the table") can never contradict each other.
  const reported = Math.floor(Number(hold.claimedSpots) || 0);
  if (reported > 0) {
    const mine = (hold.claims || []).find((row) => row.id === claimId);
    return Math.max(0, reported - Math.max(1, Math.floor(Number(mine?.partySize) || 1)));
  }
  return sumParties(others);
}

function sumParties(claims: GroupDinnerHold['claims'] | undefined): number {
  return (claims || []).reduce(
    (total, row) => total + Math.max(1, Math.floor(Number(row.partySize) || 1)),
    0,
  );
}

/** The subset of `DinnerClaimResult` the copy reads, all of it optional on the wire. */
interface ClaimEditBody {
  status?: unknown;
  availableSpots?: unknown;
  maxPartySize?: unknown;
  conflictHoldId?: unknown;
  conflictVenueName?: unknown;
  claim?: { partySize?: unknown; guestName?: unknown } | null;
}

/**
 * The result body, whether it arrived as a 2xx return value or inside a throw.
 * A refused edit is a `DinnerClaimResult` on both paths — that is the whole
 * reason this module exists rather than `mutationErrorMessage`, which finds
 * none of these keys and degrades to "Request failed with status code 409".
 */
function bodyOf(outcome: unknown): ClaimEditBody | null {
  const thrown = (outcome as { response?: { data?: unknown } })?.response?.data;
  const candidate = thrown ?? outcome;
  return candidate && typeof candidate === 'object' ? (candidate as ClaimEditBody) : null;
}

/**
 * The server's own words for a failure that is NOT a claim outcome — a 403 from
 * a non-owner, a controller-tier 400 like "Send guestName, partySize, or both."
 * Those come back as `{ error }` and the shared helper is exactly right for
 * them.
 *
 * Returns '' when the only thing `mutationErrorMessage` could find was the
 * transport string, which is how "Network Error" and "Request failed with
 * status code 500" are told apart from a real message. Neither of those is
 * usable in a restaurant, and the caller has better sentences for both.
 */
function serverMessage(outcome: unknown): string {
  const generic = mutationErrorMessage(outcome);
  if (!generic) return '';
  const transport = text((outcome as { message?: unknown })?.message);
  return generic === transport ? '' : generic;
}

/**
 * Read a per-claim edit outcome. Takes either a `DinnerClaimResult` from a 2xx
 * or the error a failed mutation threw; the wire shape is the same either way.
 */
export function editClaimNote(outcome: unknown, ctx: ClaimEditContext): ClaimEditNote {
  const body = bodyOf(outcome);
  const status = text(body?.status);
  const moving = Boolean(text(ctx.destinationVenue));
  const who = text(ctx.name) || 'That guest';
  const from = text(ctx.sourceVenue) || 'this table';
  const to = text(ctx.destinationVenue) || 'the other table';
  const wasSize = Math.max(1, Math.floor(Number(ctx.previousPartySize) || 1));

  const note = (
    severity: DinnerNoteSeverity,
    message: string,
    action?: ClaimEditAction,
  ): ClaimEditNote => (action ? { severity, message, status, action } : { severity, message, status });

  /*
    Only true on tables that have a chart, which is most of the reason
    `hasSeatingChart` is on the wire at all. The chair is genuinely left behind —
    the server's stale-assignment rule flags a seat when no capacity-reducing
    claim remains on that hold — and the existing "clear released seats" button
    on the Seating tab clears it. Saying so on a table with no chart sends a host
    looking for a screen that has nothing on it.
  */
  const chair = ctx.hasSeatingChart
    ? ' Their chair on the seating chart is kept — clear it on the Seating tab.'
    : '';

  switch (status) {
    case 'released':
      return note('success', `${who} is off the ${from} table.${chair}`);

    case 'moved':
      return note('success', `${who} moved from ${from} to ${to}.${chair}`);

    /*
      One UPDATE can carry a rename, a resize, or both, so the sentence is
      composed from what actually changed rather than switched on what was
      asked. A no-op patch also lands here — a host re-tapping the size they
      are already on, on a bad connection — and must not read as a failure.
    */
    case 'updated': {
      const size = asCount(body?.claim?.partySize);
      const renamed = text(body?.claim?.guestName);
      const parts: string[] = [];

      if (size !== null && size !== wasSize) {
        const freed = wasSize - size;
        parts.push(
          freed > 0
            ? `${who}'s party is ${size} (was ${wasSize}). ${spots(freed)} went back on the table.`
            : `${who}'s party is ${size} (was ${wasSize}).`,
        );
      }
      if (renamed && renamed !== who) parts.push(`Renamed to ${renamed}.`);

      if (parts.length === 0) {
        return note('info', `Nothing changed — ${who}'s party is still ${size ?? wasSize}.`);
      }
      return note('success', parts.join(' '));
    }

    case 'already_released':
      return note('info', `${who} was already off this table. Nothing changed.`);

    case 'already_claimed':
      return moving
        ? note('warning', `${who} already has a seat at ${to}. Nothing moved — they are still at ${from}.`)
        : note('info', `${who} already has a seat at this table. Nothing changed.`);

    case 'not_found':
      return note(
        'warning',
        `Nothing to change — ${who}'s seat is already gone. They released it themselves, or the` +
          ' table was deleted.',
      );

    case 'full': {
      if (moving) {
        return note(
          'error',
          `${to} filled up while you were looking. Nothing moved — ${who} is still at ${from}.`,
        );
      }
      const left = asCount(body?.availableSpots) ?? 0;
      return note(
        'error',
        `Only ${spots(left)} left at this table, so ${who}'s party stays at ${wasSize}.`,
      );
    }

    /*
      The refusal the host release endpoint was worth building for. Naming the
      other venue is half of it; the button that opens that guest's seat there
      is the other half, and it is what turns a dead end into two taps.
    */
    case 'dinner_conflict': {
      const venue = text(body?.conflictVenueName);
      const conflictHoldId = text(body?.conflictHoldId);
      const action: ClaimEditAction | undefined = conflictHoldId
        ? { label: venue ? `Open ${venue}` : 'Open the other table', kind: 'open-conflict', holdId: conflictHoldId }
        : undefined;

      return note(
        'warning',
        (venue
          ? `${who} already has a seat at ${venue} that evening, so nothing moved — they are still at ${from}.`
          : `${who} already has a seat at another dinner that evening and the API did not name it,` +
            ` so nothing moved — they are still at ${from}.`) + ' One table per guest per night.',
        action,
      );
    }

    case 'party_too_large': {
      const max = asCount(body?.maxPartySize);
      if (moving) {
        return note(
          'error',
          (max === null
            ? `${to} cannot take a party that size, and ${who}'s party is ${wasSize}.`
            : `${to} takes parties of at most ${max}, and ${who}'s party is ${wasSize}.`) +
            ` Nothing moved — they are still at ${from}.`,
        );
      }
      return note(
        'error',
        (max === null
          ? 'That party is too large for this table.'
          : `This table takes parties of at most ${max}.`) + ` ${who}'s party stays at ${wasSize}.`,
      );
    }

    case 'linked_to_invitee':
      return note(
        'warning',
        `${who} is on the guest list, so their name comes from there. Change it on the Guests tab` +
          ' and it changes everywhere.',
        { label: 'Open the Guests tab', kind: 'open-guests' },
      );

    case '':
      break;

    /*
      An unmapped status must print itself rather than be swallowed into
      "something went wrong" — that is how the next new refusal stays invisible
      for a release. It deliberately does NOT claim nothing moved: an unknown
      status might be a success this build has no wording for, and asserting the
      guest stayed put would be the one lie this module must not tell.
    */
    default:
      return note(
        'warning',
        `The API answered “${status}”, which Studio has no wording for.` +
          ` Reload the tables to see where ${who} is.`,
        moving ? { label: 'Reload tables', kind: 'reload' } : undefined,
      );
  }

  // No claim status on the wire at all.
  const httpStatus = Number((outcome as { response?: { status?: unknown } })?.response?.status) || 0;
  const spoke = serverMessage(outcome);
  if (spoke) {
    // A 4xx said no before it did anything; a 5xx may have died mid-write.
    if (moving && httpStatus > 0 && httpStatus < 500) {
      return { severity: 'error', message: `${spoke} Nothing moved — ${who} is still at ${from}.`, status: '' };
    }
    if (!moving) return { severity: 'error', message: spoke, status: '' };
  }

  /*
    Answered, refused, and said nothing — which is what a route the deployed API
    does not have looks like. There is no ProblemDetails middleware, so an
    unmatched route is a bare 404 with an empty body, and a non-owner host is a
    bare NotFound() too. Both used to fall through to "check your signal", which
    sends a host in a restaurant to stand by the window and retry a thing that
    will never work. The status read has to happen before `spoke`, because a
    bodyless refusal never produces a message to speak.
  */
  if (httpStatus >= 400 && httpStatus < 500) {
    return {
      severity: 'error',
      status: '',
      message:
        'The server refused that and sent no reason'
        + (moving ? `, so nothing moved — ${who} is still at ${from}.` : `, so ${who}'s seat is unchanged.`)
        + ' Either this Studio build is ahead of the API on this trip, or the trip is not yours to edit.'
        + ' Retrying will not help.',
    };
  }

  if (moving) {
    return {
      severity: 'error',
      message:
        `Studio lost the connection before it heard back. ${who} may or may not have moved —` +
        ' reload the tables before trying again.',
      status: '',
      action: { label: 'Reload tables', kind: 'reload' },
    };
  }
  return {
    severity: 'error',
    message: `That did not reach the server — ${who}'s seat is unchanged. Check your signal and try again.`,
    status: '',
  };
}

export interface MoveDestination {
  hold: GroupDinnerHold;
  /** Same local date as the table the guest is on now. */
  sameEvening: boolean;
}

/**
 * The tables a guest can be moved to, same evening first.
 *
 * The ordering is presentation, NOT the one-table-per-evening rule: a party
 * changing tables tonight is the actual operation a host performs standing up,
 * so those tables go to the top. Studio never pre-filters the destinations a
 * same-evening conflict would refuse — that predicate lives on the server, a
 * copy of it here would be a second rule that can disagree, and a same-evening
 * A-to-B move is the legitimate case, not the forbidden one.
 *
 * Full tables stay in the list too (the caller disables them and says "full"):
 * a table that vanishes explains nothing.
 */
export function moveDestinations(
  holds: readonly GroupDinnerHold[],
  fromHold: Pick<GroupDinnerHold, 'id' | 'reservedAt' | 'timeZone'>,
  tripTimeZone: string,
): MoveDestination[] {
  const localDate = (hold: Pick<GroupDinnerHold, 'reservedAt' | 'timeZone'>): string =>
    isoToWallClock(hold.reservedAt, hold.timeZone || tripTimeZone).slice(0, 10);

  const fromDate = localDate(fromHold);

  return (holds || [])
    .filter((hold) => hold.id !== fromHold.id && hold.status !== 'cancelled')
    .map((hold) => ({
      hold,
      sameEvening: Boolean(fromDate) && localDate(hold) === fromDate,
    }))
    .sort((a, b) => {
      if (a.sameEvening !== b.sameEvening) return a.sameEvening ? -1 : 1;
      return (a.hold.reservedAt || '').localeCompare(b.hold.reservedAt || '');
    });
}
